import React, { useEffect, useMemo, useRef, useState } from "react"

import { useMutation, useQuery } from "@tanstack/react-query"
import { format } from "date-fns"
import { AlertCircle, Calendar, Check, Edit, Users } from "lucide-react"
import { AlertTriangle } from "lucide-react"
import { ChevronsUpDown } from "lucide-react"
import { toast } from "sonner"

import SheriffTypeChangeWarningDialog from "@/components/people/SheriffTypeChangeWarningDialog"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Calendar as CalendarComponent } from "@/components/ui/calendar"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Dialog as ConfirmDialog,
  DialogContent as ConfirmDialogContent,
  DialogHeader as ConfirmDialogHeader,
  DialogTitle as ConfirmDialogTitle,
} from "@/components/ui/dialog"
import { MultiSelect } from "@/components/ui/multi-select"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { ukToday } from "@/constants/sheriffOptions"
import { useAuth } from "@/contexts/AuthContext"
import { type UpcomingShift } from "@/hooks/useActiveEngagements"
import { useDepots } from "@/hooks/useDepots"
import { useFetchPeople } from "@/hooks/useFetchPeople"
import { useFetchMismatchedUpcomingShifts } from "@/hooks/useMismatchedUpcomingShifts"
import { useCanReactivatePerson, useReactivatePerson } from "@/hooks/useReactivatePerson"
import { useStickyLocations } from "@/hooks/useStickyLocations"
import { useSubTeams } from "@/hooks/useSubTeams"
import { useUpdatePerson } from "@/hooks/useUpdatePerson"
import { supabase } from "@/lib/supabase"
import { cn } from "@/lib/utils"
import { Person } from "@/types/person"
import { deactivatedLabel, depotPickerOptions } from "@/utils/depotValidity"
import { formatPersonShortName } from "@/utils/personDisplay"

import type { Enums, TablesUpdate } from "../../../../database.types"

interface PersonTypeDetailsTabProps {
  person: Person
  onSelectPerson: (person: Person) => void
}

const formatDate = (value: string | null) => {
  if (!value) return "Not provided"
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? value : format(parsed, "PPP")
}

const formatList = (value?: string[] | null) => {
  if (!value || value.length === 0) return "Not provided"
  return value.join(", ")
}

const calculateTimeSinceStartDate = (startDate: string | null): string => {
  if (!startDate) return "Not calculated"

  const start = new Date(startDate)
  if (Number.isNaN(start.getTime())) return "Invalid date"

  const now = new Date()
  const diffMs = now.getTime() - start.getTime()

  if (diffMs < 0) return "Future date"

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const years = Math.floor(diffDays / 365)
  const months = Math.floor((diffDays % 365) / 30)
  const days = Math.floor((diffDays % 365) % 30)

  const parts: string[] = []
  if (years > 0) parts.push(`${years} year${years !== 1 ? "s" : ""}`)
  if (months > 0) parts.push(`${months} month${months !== 1 ? "s" : ""}`)
  if (days > 0 || parts.length === 0) parts.push(`${days} day${days !== 1 ? "s" : ""}`)

  return parts.join(", ")
}

const PersonTypeDetailsTab: React.FC<PersonTypeDetailsTabProps> = ({ person, onSelectPerson }) => {
  // Fetch sticky locations from database
  const { stickyLocations } = useStickyLocations()

  // Fetch substitute drivers that have this person set as their primary.
  const { data: substituteDrivers = [], isLoading: substitutesLoading } = useQuery<Person[]>({
    queryKey: ["substitute-drivers", person.id],
    enabled: !!person.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("people")
        .select("*, has_multiple_locations")
        .eq("primary_person_id", person.id)
        .order("first_name", { ascending: true })

      if (error) throw new Error(error.message)
      return data || []
    },
  })
  const [personData, setPersonData] = useState(person)
  const [openDialog, setOpenDialog] = useState<string | null>(null)
  const [formState, setFormState] = useState({ ...person })
  const { data: depots = [], isLoading: depotsLoading } = useDepots()
  // Every depot, with closed ones last and disabled (labelled with when they closed). One already
  // on the person can still be removed.
  const depotOptions = useMemo(
    () =>
      depotPickerOptions(depots, ukToday()).map(({ depot, closedOn }) => ({
        value: depot.name,
        label: closedOn ? `${depot.name} ${deactivatedLabel(closedOn)}` : depot.name,
        disabled: Boolean(closedOn),
      })),
    [depots],
  )
  const { subTeamOptionsWithIds, loading: subTeamsLoading } = useSubTeams()
  const [stickyPopoverOpen, setStickyPopoverOpen] = useState(false)
  const stickyTriggerRef = useRef<HTMLButtonElement | null>(null)
  const [stickyContentWidth, setStickyContentWidth] = useState<number>()
  const [stickySearchQuery, setStickySearchQuery] = useState("")
  const [trainerPopoverOpen, setTrainerPopoverOpen] = useState(false)
  const trainerTriggerRef = useRef<HTMLButtonElement | null>(null)
  const [trainerContentWidth, setTrainerContentWidth] = useState<number>()
  const [trainerSearchQuery, setTrainerSearchQuery] = useState("")
  const { data: activePeopleData } = useFetchPeople({
    statusFilter: "Active",
    searchQuery: trainerSearchQuery,
  })
  const { updatePerson, isUpdating, updateError } = useUpdatePerson()
  const { claims } = useAuth()
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({})
  const [offboardingReason, setOffboardingReason] = useState("")
  const [offboardingNotes, setOffboardingNotes] = useState("")
  const [showConfirmOffboarding, setShowConfirmOffboarding] = useState(false)
  const [showConfirmReactivate, setShowConfirmReactivate] = useState(false)
  const isOffboarded = personData.status === "Inactive" || personData.status === "Banned"
  const { data: canReactivate = false } = useCanReactivatePerson(personData.id, isOffboarded)
  const { mutate: reactivatePerson, isPending: isReactivating } = useReactivatePerson()
  const { mutateAsync: checkMismatchedUpcomingShifts, isPending: isCheckingShifts } = useMutation({
    mutationFn: useFetchMismatchedUpcomingShifts(),
  })
  const [pendingSheriffChange, setPendingSheriffChange] = useState<{
    fields: TablesUpdate<"people">
    upcomingShifts: UpcomingShift[]
  } | null>(null)

  const teamPermissions = claims.team_permissions || []
  const canEditPersonType = teamPermissions.includes("update.tab.person_type")

  useEffect(() => {
    setPersonData(person)
  }, [person])

  const handleOpenDialog = (dialogName: string) => {
    setFormState({ ...personData })
    setOpenDialog(dialogName)
  }

  const resetEditDialog = () => {
    setFormState({ ...personData })
    setOffboardingReason("")
    setOffboardingNotes("")
    setFormErrors({})
    setShowConfirmOffboarding(false)
    setPendingSheriffChange(null)
    setOpenDialog(null)
  }

  // Helper functions for date handling
  const parseDate = (dateStr: string | null): Date | undefined => {
    if (!dateStr) return undefined
    if (/^(\d{4})-(\d{2})-(\d{2})$/.test(dateStr)) {
      return new Date(
        Number(dateStr.slice(0, 4)),
        Number(dateStr.slice(5, 7)) - 1,
        Number(dateStr.slice(8, 10)),
      )
    }
    return new Date(dateStr)
  }

  const selectedStartDate = parseDate(formState.contract_start_date)
  const selectedEndDate = parseDate(formState.contract_end_date)

  const handleStartDateSelect = (date?: Date) => {
    setFormState((prev) => ({
      ...prev,
      contract_start_date: date ? format(date, "yyyy-MM-dd") : null,
    }))
  }

  const handleEndDateSelect = (date?: Date) => {
    setFormState((prev) => ({
      ...prev,
      contract_end_date: date ? format(date, "yyyy-MM-dd") : null,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canEditPersonType) return

    try {
      const fields: TablesUpdate<"people"> = {}

      if (openDialog === "person-type") {
        if (formState.status !== personData.status) {
          fields.status = formState.status
        }
        if (formState.contract_type !== personData.contract_type) {
          fields.contract_type = formState.contract_type
        }
        if (formState.pay_basis !== personData.pay_basis) {
          fields.pay_basis = formState.pay_basis
        }
        if (formState.contract_start_date !== personData.contract_start_date) {
          fields.contract_start_date = formState.contract_start_date
        }
        if (formState.contract_end_date !== personData.contract_end_date) {
          fields.contract_end_date = formState.contract_end_date
        }
        // person field is not editable
      } else if (openDialog === "sheriff") {
        const nextAreas = formState.sheriff_areas ?? null
        const currentAreas = personData.sheriff_areas ?? null
        if (JSON.stringify(nextAreas) !== JSON.stringify(currentAreas)) {
          fields.sheriff_areas = nextAreas
        }
        if (formState.sheriff_type !== personData.sheriff_type) {
          fields.sheriff_type = formState.sheriff_type
        }
        const nextLocation = formState.location ?? null
        const prevLocation = personData.location ?? null
        if (JSON.stringify(nextLocation) !== JSON.stringify(prevLocation)) {
          fields.location = nextLocation
        }
        if (formState.sticky_location !== personData.sticky_location) {
          fields.sticky_location = formState.sticky_location
        }
        if (formState.driver_manager_team !== personData.driver_manager_team) {
          fields.driver_manager_team = formState.driver_manager_team
        }
      } else if (openDialog === "performance") {
        if (formState.driver_status_internal !== personData.driver_status_internal) {
          fields.driver_status_internal = formState.driver_status_internal
        }
        if (formState.hpd_pool !== personData.hpd_pool) {
          fields.hpd_pool = formState.hpd_pool
        }
        if (
          (formState.efficiency_multiplier ?? null) !== (personData.efficiency_multiplier ?? null)
        ) {
          fields.efficiency_multiplier = formState.efficiency_multiplier ?? null
        }
        if ((formState.min_commitment ?? null) !== (personData.min_commitment ?? null)) {
          fields.min_commitment = formState.min_commitment ?? null
        }
        if ((formState.max_duration ?? null) !== (personData.max_duration ?? null)) {
          fields.max_duration = formState.max_duration ?? null
        }
        if ((formState.unassigned_cost ?? null) !== (personData.unassigned_cost ?? null)) {
          fields.unassigned_cost = formState.unassigned_cost ?? null
        }
        if (formState.driver_trainer_responsible !== personData.driver_trainer_responsible) {
          fields.driver_trainer_responsible = formState.driver_trainer_responsible
        }
      }

      if (openDialog === "sheriff" && fields.sheriff_type) {
        try {
          const upcomingShifts = await checkMismatchedUpcomingShifts({
            personId: personData.id,
            sheriffType: fields.sheriff_type,
          })
          if (upcomingShifts.length > 0) {
            setPendingSheriffChange({ fields, upcomingShifts })
            return
          }
        } catch {
          toast.warning("Could not check upcoming shifts", {
            description:
              "Saving anyway. Upcoming shifts that don't match the new sheriff type will be cancelled.",
          })
        }
      }

      await saveFields(fields)
    } catch (error) {
      console.error("Error updating details:", error)
    }
  }

  const saveFields = async (fields: TablesUpdate<"people">) => {
    if (Object.keys(fields).length > 0) {
      const response = await updatePerson({
        id: personData.id,
        fields,
      })
      const updatedPerson = { ...personData, ...response }
      setFormState(updatedPerson)
      setPersonData(updatedPerson)
    }
    setOpenDialog(null)
  }

  const handleConfirmSheriffChange = async () => {
    if (!pendingSheriffChange) return
    try {
      await saveFields(pendingSheriffChange.fields)
    } catch (error) {
      console.error("Error updating details:", error)
    } finally {
      setPendingSheriffChange(null)
    }
  }

  return (
    <div className="space-y-6">
      {updateError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {updateError instanceof Error ? updateError.message : "Failed to update"}
          </AlertDescription>
        </Alert>
      )}

      {/* Person Type Information */}
      <Dialog
        open={openDialog === "person-type"}
        onOpenChange={(v) => (v ? handleOpenDialog("person-type") : setOpenDialog(null))}
      >
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Person Type Information</CardTitle>
            {canEditPersonType && (
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </DialogTrigger>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {isOffboarded ? (
                <div className="md:col-span-2">
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-4 w-full mb-2">
                    <span
                      className="inline-block text-base font-bold px-4 py-2 rounded bg-red-100 text-red-800"
                      style={{ minWidth: 120, textAlign: "center" }}
                    >
                      {personData.status}
                    </span>
                    <div className="flex-1 w-full">
                      <div className="bg-palette-jungleGreen000 border border-palette-jungleGreen200 rounded px-4 py-3">
                        <div className="space-y-1">
                          <div className="text-xs text-palette-jungleGreen700">
                            <span className="font-medium">Reason:</span>{" "}
                            {personData.offboarding_reason || "Not provided"}
                          </div>
                          <div className="text-xs text-palette-jungleGreen700">
                            <span className="font-medium">Date:</span>{" "}
                            {personData.contract_end_date || "Not provided"}
                          </div>
                          <div className="text-xs text-palette-jungleGreen700">
                            <span className="font-medium">Notes:</span>{" "}
                            {personData.offboarding_notes || "Not provided"}
                          </div>
                        </div>
                      </div>
                    </div>
                    {canReactivate && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowConfirmReactivate(true)}
                      >
                        Reactivate
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="text-sm font-medium text-palette-jungleGreen700">Status</label>
                  <div className="mt-1">
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                      {personData.status || "Not provided"}
                    </span>
                  </div>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Role</label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.role || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Contract Type
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.contract_type || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Payment Basis
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.payment_basis || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Contract Start Date
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {formatDate(personData.contract_start_date)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Contract End Date
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {formatDate(personData.contract_end_date)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Time Since Start Date
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {calculateTimeSinceStartDate(personData.contract_start_date)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Person</label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.person || "Not provided"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Person Type Information</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              const isOffboarding = formState.status === "Inactive" || formState.status === "Banned"
              let valid = true
              const errors: { [key: string]: string } = {}
              if (isOffboarding) {
                if (!formState.contract_end_date) {
                  errors.contract_end_date = "Contract End Date is required."
                  valid = false
                }
                if (!offboardingReason) {
                  errors.offboardingReason = "Reason for Offboarding is required."
                  valid = false
                }
              }
              setFormErrors(errors)
              if (valid) {
                if (isOffboarding) {
                  setShowConfirmOffboarding(true)
                } else {
                  handleSubmit(e)
                }
              }
            }}
          >
            {formState.status === "Inactive" || formState.status === "Banned" ? (
              <>
                <div className="bg-yellow-100 border-l-4 border-yellow-500 p-4 mb-4 flex items-start gap-3 rounded">
                  <AlertTriangle className="h-6 w-6 text-yellow-600 mt-0.5" />
                  <div>
                    <div className="font-semibold text-yellow-800">
                      This will offboard the person from our systems.
                    </div>
                    <div className="text-yellow-700 text-sm">
                      Please confirm the details below before proceeding.
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-palette-jungleGreen700">
                      Reason for Offboarding <span className="text-red-500">*</span>
                    </label>
                    <Select value={offboardingReason} onValueChange={setOffboardingReason}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent position="popper" sideOffset={5}>
                        <SelectItem value="Contractor ended engagement">
                          Contractor ended engagement
                        </SelectItem>
                        <SelectItem value="HIVED ended engagement">
                          HIVED ended engagement
                        </SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {formErrors?.offboardingReason && (
                      <div className="text-xs text-red-600 mt-1">
                        {formErrors.offboardingReason}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-palette-jungleGreen700">
                      Contract End Date <span className="text-red-500">*</span>
                    </label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {selectedEndDate ? format(selectedEndDate, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={selectedEndDate}
                          onSelect={handleEndDateSelect}
                          weekStartsOn={1}
                          captionLayout="dropdown"
                          fromYear={2000}
                        />
                      </PopoverContent>
                    </Popover>
                    {formErrors?.contract_end_date && (
                      <div className="text-xs text-red-600 mt-1">
                        {formErrors.contract_end_date}
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-palette-jungleGreen700">
                      Offboarding Notes
                    </label>
                    <Textarea
                      value={offboardingNotes}
                      onChange={(e) => setOffboardingNotes(e.target.value)}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-palette-jungleGreen700">Status</label>
                  <Select
                    value={formState.status || ""}
                    onValueChange={(value) => setFormState({ ...formState, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={5}>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                      <SelectItem value="Banned">Banned</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-palette-jungleGreen700">
                    Contract Start Date
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {selectedStartDate ? format(selectedStartDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={selectedStartDate}
                        onSelect={handleStartDateSelect}
                        weekStartsOn={1}
                        captionLayout="dropdown"
                        fromYear={2000}
                        toYear={new Date().getFullYear() + 10}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            )}
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={resetEditDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={isUpdating}>
                {isUpdating ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
          {/* Confirm dialog for offboarding */}
          {showConfirmOffboarding && (
            <ConfirmDialog open={showConfirmOffboarding} onOpenChange={setShowConfirmOffboarding}>
              <ConfirmDialogContent>
                <ConfirmDialogHeader>
                  <ConfirmDialogTitle>Confirm Offboarding</ConfirmDialogTitle>
                </ConfirmDialogHeader>
                <div className="py-4 text-palette-jungleGreen800">
                  Are you sure you want to offboard this person? This action cannot be undone.
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" onClick={resetEditDialog}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      try {
                        const fields = {
                          status: formState.status,
                          contract_end_date: formState.contract_end_date,
                          offboarding_reason: offboardingReason,
                          offboarding_notes: offboardingNotes,
                        }

                        const response = await updatePerson({
                          id: personData.id,
                          fields: fields,
                        })

                        const updatedPerson = { ...personData, ...response }
                        setFormState(updatedPerson)
                        setPersonData(updatedPerson)
                        resetEditDialog()
                      } catch (error) {
                        console.error("Error during offboarding:", error)
                      }
                    }}
                  >
                    Confirm Offboarding
                  </Button>
                </div>
              </ConfirmDialogContent>
            </ConfirmDialog>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm dialog for reactivating an offboarded person */}
      <ConfirmDialog open={showConfirmReactivate} onOpenChange={setShowConfirmReactivate}>
        <ConfirmDialogContent>
          <ConfirmDialogHeader>
            <ConfirmDialogTitle>Reactivate {formatPersonShortName(personData)}?</ConfirmDialogTitle>
          </ConfirmDialogHeader>
          <div className="py-4 text-palette-jungleGreen800">
            Their status will be set back to Active, and their offboarding reason, notes and
            contract end date will be cleared. Blocks unassigned when they were offboarded are not
            restored.
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowConfirmReactivate(false)}
              disabled={isReactivating}
            >
              Cancel
            </Button>
            <Button
              disabled={isReactivating}
              onClick={() =>
                reactivatePerson(personData.id, {
                  onSuccess: () => setShowConfirmReactivate(false),
                })
              }
            >
              {isReactivating ? "Reactivating..." : "Reactivate"}
            </Button>
          </div>
        </ConfirmDialogContent>
      </ConfirmDialog>

      {/* Sheriff & Location Details */}
      <Dialog
        open={openDialog === "sheriff"}
        onOpenChange={(v) => (v ? handleOpenDialog("sheriff") : setOpenDialog(null))}
      >
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Sheriff & Location Details</CardTitle>
            {canEditPersonType && (
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </DialogTrigger>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Sheriff Areas
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {formatList(personData.sheriff_areas)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Sheriff Type
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.sheriff_type || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Location</label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {formatList(personData.location)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Sticky Location
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.sticky_location || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Driver Manager Team
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {(() => {
                    if (
                      !personData.driver_manager_team ||
                      personData.driver_manager_team === "None"
                    ) {
                      return "Not provided"
                    }
                    // Try to find the team name from subTeamOptionsWithIds
                    const teamOption = subTeamOptionsWithIds.find(
                      (option) =>
                        option.type === "DRIVER_MANAGER_TEAM" &&
                        (option.value === personData.driver_manager_team ||
                          option.id === personData.driver_manager_team),
                    )
                    return teamOption?.label || personData.driver_manager_team
                  })()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Sheriff & Location Details</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Sheriff Areas
                </label>
                <MultiSelect
                  options={[
                    { value: "Rc1 Deliveries", label: "RC1 Deliveries" },
                    { value: "Rc2 Deliveries", label: "RC2 Deliveries" },
                    { value: "Collections", label: "Collections" },
                    { value: "Fleet Team", label: "Fleet Team" },
                    { value: "Sortation", label: "Sortation" },
                    { value: "Depot", label: "Depot" },
                    { value: "Live Ops", label: "Live Ops" },
                    { value: "Network", label: "Network" },
                    { value: "Customer Service", label: "Customer Service" },
                    { value: "Driver Ops", label: "Driver Ops" },
                    {
                      value: "Driver Partnerships",
                      label: "Driver Partnerships",
                    },
                    { value: "Driver Resourcing", label: "Driver Resourcing" },
                    { value: "Dsp Managers", label: "DSP Managers" },
                    { value: "Cx", label: "CX" },
                    { value: "Des", label: "DES" },
                    { value: "Hq", label: "HQ" },
                  ]}
                  selected={Array.isArray(formState.sheriff_areas) ? formState.sheriff_areas : []}
                  onChange={(values) =>
                    setFormState({
                      ...formState,
                      sheriff_areas: values.length > 0 ? (values as Enums<"person_areas">[]) : null,
                    })
                  }
                  placeholder="Select sheriff areas"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Sheriff Type
                </label>
                <Select
                  value={formState.sheriff_type || ""}
                  onValueChange={(value) =>
                    setFormState({ ...formState, sheriff_type: value as Enums<"person_type"> })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="HivedVan">HivedVan</SelectItem>
                    <SelectItem value="HivedHgv">HivedHgv</SelectItem>
                    <SelectItem value="HivedFlex">HivedFlex</SelectItem>
                    <SelectItem value="TakeHomeVan">TakeHomeVan</SelectItem>
                    <SelectItem value="Dsp">DSP</SelectItem>
                    <SelectItem value="Trial">Trial</SelectItem>
                    <SelectItem value="Internal">Internal</SelectItem>
                    <SelectItem value="AssociateDriver">Associate Driver</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-palette-jungleGreen700">Location</label>
                <MultiSelect
                  options={depotsLoading ? [] : depotOptions}
                  selected={Array.isArray(formState.location) ? formState.location : []}
                  onChange={(values) =>
                    setFormState({
                      ...formState,
                      location: values.length > 0 ? values : null,
                    })
                  }
                  placeholder={depotsLoading ? "Loading depots..." : "Select locations"}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Sticky Location
                </label>
                <Popover
                  open={stickyPopoverOpen}
                  onOpenChange={(open) => {
                    if (!open) {
                      setStickyPopoverOpen(false)
                      return
                    }
                    setStickyPopoverOpen(true)
                    requestAnimationFrame(() => {
                      if (stickyTriggerRef.current) {
                        setStickyContentWidth(stickyTriggerRef.current.offsetWidth)
                      }
                    })
                  }}
                >
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      ref={stickyTriggerRef}
                      className={cn(
                        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm",
                        "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      )}
                    >
                      <span className="truncate">
                        {formState.sticky_location || "Select sticky location"}
                      </span>
                      <ChevronsUpDown className="h-4 w-4 opacity-50" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="p-0"
                    align="start"
                    style={{ width: stickyContentWidth }}
                    sideOffset={4}
                    onWheel={(event) => {
                      event.stopPropagation()
                    }}
                  >
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Search location..."
                        value={stickySearchQuery}
                        onValueChange={setStickySearchQuery}
                      />
                      <CommandEmpty>No location found.</CommandEmpty>
                      <CommandGroup>
                        <div className="max-h-64 overflow-y-auto">
                          {stickyLocations
                            .filter((loc) =>
                              loc.code.toLowerCase().includes(stickySearchQuery.toLowerCase()),
                            )
                            .map((loc) => {
                              const isSelected = formState.sticky_location === loc.code
                              return (
                                <CommandItem
                                  key={loc.id}
                                  value={loc.code}
                                  onSelect={() => {
                                    setFormState({
                                      ...formState,
                                      sticky_location: loc.code ? loc.code : null,
                                    })
                                    setStickyPopoverOpen(false)
                                  }}
                                >
                                  <span>{loc.code}</span>
                                  {isSelected && <Check className="ml-auto h-4 w-4" />}
                                </CommandItem>
                              )
                            })}
                        </div>
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Driver Manager Team
                </label>
                <Select
                  value={formState.driver_manager_team || "None"}
                  onValueChange={(value) =>
                    setFormState({
                      ...formState,
                      driver_manager_team: value === "None" ? null : value,
                    })
                  }
                  disabled={subTeamsLoading}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={subTeamsLoading ? "Loading..." : "Select driver manager team"}
                    />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="None">None</SelectItem>
                    {subTeamOptionsWithIds
                      .filter((option) => option.type === "DRIVER_MANAGER_TEAM")
                      .map((option) => (
                        <SelectItem key={option.id} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setOpenDialog(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isUpdating || isCheckingShifts}>
                {isCheckingShifts ? (
                  <>Checking shifts...</>
                ) : isUpdating ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
          <SheriffTypeChangeWarningDialog
            open={!!pendingSheriffChange}
            onOpenChange={(open) => {
              if (!open) setPendingSheriffChange(null)
            }}
            personName={`${personData.first_name || ""} ${personData.surname || ""}`.trim()}
            upcomingShifts={pendingSheriffChange?.upcomingShifts ?? []}
            onConfirm={handleConfirmSheriffChange}
            isConfirming={isUpdating}
          />
        </DialogContent>
      </Dialog>

      {/* Performance & Configuration */}
      <Dialog
        open={openDialog === "performance"}
        onOpenChange={(v) => (v ? handleOpenDialog("performance") : setOpenDialog(null))}
      >
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Performance & Configuration</CardTitle>
            {canEditPersonType && (
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </DialogTrigger>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Driver Status (Internal)
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.driver_status_internal || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Driver Trainer Responsible
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.driver_trainer_responsible || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">HPD Pool</label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.hpd_pool || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Efficiency Multiplier
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.efficiency_multiplier ?? "Not set"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Min Commitment
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.min_commitment ?? "Not set"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Max Duration
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.max_duration ?? "Not set"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Unassigned Cost
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.unassigned_cost ?? "Not set"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Performance & Configuration</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Driver Status (Internal)
                </label>
                <Select
                  value={formState.driver_status_internal || "_none"}
                  onValueChange={(value) =>
                    setFormState({
                      ...formState,
                      driver_status_internal: value === "_none" ? null : value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select driver status" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="_none">None</SelectItem>
                    <SelectItem value="New Driver">New Driver</SelectItem>
                    <SelectItem value="Informal Call">Informal Call</SelectItem>
                    <SelectItem value="Watch List">Watch List</SelectItem>
                    <SelectItem value="First SLA Breach">First SLA Breach</SelectItem>
                    <SelectItem value="Second SLA Breach">Second SLA Breach</SelectItem>
                    <SelectItem value="To Be Off-boarded">To Be Off-boarded</SelectItem>
                    <SelectItem value="Contractor Ending Engagement">
                      Contractor Ending Engagement
                    </SelectItem>
                    <SelectItem value="At Risk of Leaving">At Risk of Leaving</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Driver Trainer Responsible
                </label>
                <Popover
                  open={trainerPopoverOpen}
                  onOpenChange={(open) => {
                    if (!open) {
                      setTrainerPopoverOpen(false)
                      setTrainerSearchQuery("")
                      return
                    }
                    setTrainerPopoverOpen(true)
                    requestAnimationFrame(() => {
                      if (trainerTriggerRef.current) {
                        setTrainerContentWidth(trainerTriggerRef.current.offsetWidth)
                      }
                    })
                  }}
                >
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      ref={trainerTriggerRef}
                      className={cn(
                        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm",
                        "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      )}
                    >
                      <span className="truncate">
                        {formState.driver_trainer_responsible || "Select trainer"}
                      </span>
                      <ChevronsUpDown className="h-4 w-4 opacity-50" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="p-0 w-full"
                    align="start"
                    style={{ width: trainerContentWidth }}
                    sideOffset={4}
                    onWheel={(event) => {
                      event.stopPropagation()
                    }}
                  >
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Search people..."
                        value={trainerSearchQuery}
                        onValueChange={setTrainerSearchQuery}
                      />
                      <CommandEmpty>No person found.</CommandEmpty>
                      <CommandGroup>
                        <div className="max-h-64 overflow-y-auto">
                          {activePeopleData?.pages
                            .flatMap((page) => page.people)
                            .map((person) => {
                              const isSelected =
                                formState.driver_trainer_responsible === person.email
                              return (
                                <CommandItem
                                  key={person.id}
                                  value={person.person || person.id}
                                  onSelect={() => {
                                    setFormState({
                                      ...formState,
                                      driver_trainer_responsible: person.email || null,
                                    })
                                    setTrainerPopoverOpen(false)
                                  }}
                                >
                                  <span>
                                    {person.person || `${person.first_name} ${person.surname}`}
                                  </span>
                                  {isSelected && <Check className="ml-auto h-4 w-4" />}
                                </CommandItem>
                              )
                            })}
                        </div>
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">HPD Pool</label>
                <Select
                  value={formState.hpd_pool || ""}
                  onValueChange={(value) => setFormState({ ...formState, hpd_pool: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select HPD pool" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="a">a</SelectItem>
                    <SelectItem value="b">b</SelectItem>
                    <SelectItem value="c">c</SelectItem>
                    <SelectItem value="d">d</SelectItem>
                    <SelectItem value="e">e</SelectItem>
                    <SelectItem value="f">f</SelectItem>
                    <SelectItem value="Flex">Flex</SelectItem>
                    <SelectItem value="T/S">T/S</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOpenDialog(null)
                  setTrainerSearchQuery("")
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isUpdating}>
                {isUpdating ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Substitute Drivers */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Substitute Drivers
          </CardTitle>
        </CardHeader>
        <CardContent>
          {substitutesLoading ? (
            <p className="text-sm text-palette-jungleGreen500">Loading substitute drivers...</p>
          ) : substituteDrivers.length === 0 ? (
            <p className="text-sm text-palette-jungleGreen500">
              No substitute drivers assigned to this person.
            </p>
          ) : (
            <ul className="divide-y divide-palette-jungleGreen100">
              {substituteDrivers.map((sub) => (
                <li key={sub.id}>
                  <button
                    type="button"
                    onClick={() => onSelectPerson(sub)}
                    className="w-full text-left py-2 px-1 flex items-center justify-between rounded cursor-pointer"
                  >
                    <span className="text-sm text-palette-jungleGreen900">
                      {formatPersonShortName(sub)}
                    </span>
                    {sub.status && (
                      <span className="text-xs text-palette-jungleGreen900">{sub.status}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default PersonTypeDetailsTab
